<vs-content-card *ngIf="warranty" [header]="{ title: 'Your documents' }">
  <button *ngIf="canUpload" headerEnd vs-button type="button" size="sm" (click)="uploadDialog.open()">
    Upload document
  </button>
  <div class="p-5" [attr.aria-busy]="loading">
    <form
      [formGroup]="filters"
      aria-label="Filter documents"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end mb-5 pb-5 border-b border-gray-200"
    >
      <vs-form-field [config]="{ label: 'Category' }">
        <vs-select
          vsInput
          formControlName="category"
          [config]="{ placeholder: 'All categories' }"
          aria-label="Category"
        >
          <li vs-select-option [value]="''">All categories</li>
          <li *ngFor="let category of categories" vs-select-option [value]="category">
            {{ categoryLabels[category] }}
          </li>
        </vs-select>
      </vs-form-field>
      <vs-form-field [config]="{ label: 'Service date from' }">
        <input
          vsInput
          type="date"
          [showDefaultPicker]="true"
          formControlName="dateFrom"
          [attr.max]="filters.controls.dateTo.value || null"
          aria-label="Service date from"
        />
      </vs-form-field>
      <vs-form-field [config]="{ label: 'Service date to' }">
        <input
          vsInput
          type="date"
          [showDefaultPicker]="true"
          formControlName="dateTo"
          [attr.min]="filters.controls.dateFrom.value || null"
          aria-label="Service date to"
        />
      </vs-form-field>
      <vs-form-field *ngIf="warranty.assetId && warranty.asset?.name" [config]="{ label: 'Covered item' }">
        <vs-select
          vsInput
          formControlName="assetId"
          [config]="{ placeholder: 'All documents' }"
          aria-label="Covered item"
        >
          <li vs-select-option [value]="''">All documents</li>
          <li vs-select-option [value]="warranty.assetId">{{ warranty.asset?.name }}</li>
        </vs-select>
      </vs-form-field>
      <div *ngIf="hasFilters" class="sm:col-span-2 lg:col-span-4">
        <button vs-button appearance="outline" size="sm" type="button" (click)="clearFilters()">Clear filters</button>
      </div>
    </form>

    <p *ngIf="invalidDateRange" role="alert" class="py-4 text-sm text-error-800">
      The start date must be on or before the end date.
    </p>

    <div *ngIf="loading" role="status" class="flex items-center justify-center gap-3 py-8 text-sm text-gray-500">
      <span class="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" aria-hidden="true"></span>
      Loading documents…
    </div>

    <div *ngIf="error" role="alert" class="flex flex-wrap items-center justify-between gap-3 py-4">
      <p class="text-sm text-error-800">Unable to load your documents. Please try again.</p>
      <button vs-button appearance="outline" size="sm" type="button" (click)="loadDocuments()">Retry</button>
    </div>

    <ng-container *ngIf="!loading && !error && !invalidDateRange">
      <p *ngIf="!documents.length" role="status" class="py-8 text-center text-sm text-gray-500">
        {{ hasFilters ? 'No documents match these filters.' : 'No documents uploaded yet.' }}
      </p>

      <ul *ngIf="documents.length" class="divide-y divide-gray-200" aria-label="Your documents">
        <li *ngFor="let document of documents; trackBy: trackById" class="py-4 first:pt-0 last:pb-0">
          <h3 class="font-semibold text-gray-900 break-words">{{ document.title }}</h3>
          <dl class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt class="text-gray-500">Category</dt>
              <dd class="text-gray-900">{{ categoryLabels[document.category] }}</dd>
            </div>
            <div>
              <dt class="text-gray-500">Uploaded</dt>
              <dd class="text-gray-900">{{ document.uploadedAt | date: 'mediumDate' : 'UTC' }}</dd>
            </div>
            <div *ngIf="document.serviceDate">
              <dt class="text-gray-500">Service/inspection date</dt>
              <dd class="text-gray-900">{{ document.serviceDate + 'T00:00:00Z' | date: 'mediumDate' : 'UTC' }}</dd>
            </div>
            <div *ngIf="document.assetId && document.assetId === warranty?.assetId && warranty?.asset?.name">
              <dt class="text-gray-500">Covered asset</dt>
              <dd class="text-gray-900 break-words">{{ warranty?.asset?.name }}</dd>
            </div>
          </dl>
          <div class="mt-4 flex flex-wrap gap-3">
            <button
              vs-button
              appearance="outline"
              size="sm"
              type="button"
              [disabled]="!!previewingId"
              title="Open in a new tab"
              [attr.aria-label]="'Preview ' + document.title + ' in a new tab'"
              (click)="openPreview(document)"
            >
              {{ previewingId === document.id ? 'Preparing preview…' : 'Preview' }}
            </button>
            <button
              vs-button
              appearance="outline"
              size="sm"
              type="button"
              [disabled]="!!downloadingId"
              (click)="downloadDocument(document)"
            >
              {{ downloadingId === document.id ? 'Preparing download…' : 'Download' }}
            </button>
            <button
              vs-button
              appearance="outline"
              size="sm"
              theme="danger"
              type="button"
              [disabled]="removing"
              (click)="confirmRemoval(document)"
            >
              Remove
            </button>
          </div>
        </li>
      </ul>

      <nav
        *ngIf="totalPages > 1"
        aria-label="Document pages"
        class="flex items-center justify-between gap-3 mt-5 border-t border-gray-200 pt-4"
      >
        <button
          vs-button
          appearance="outline"
          size="sm"
          type="button"
          [disabled]="page <= 1 || removing"
          (click)="changePage(page - 1)"
        >
          Previous
        </button>
        <span class="text-sm text-gray-600" aria-live="polite">Page {{ page }} of {{ totalPages }}</span>
        <button
          vs-button
          appearance="outline"
          size="sm"
          type="button"
          [disabled]="page >= totalPages || removing"
          (click)="changePage(page + 1)"
        >
          Next
        </button>
      </nav>
    </ng-container>
  </div>
</vs-content-card>

<ws-upload-warranty-document-dialog
  #uploadDialog
  [warranty]="warranty"
  (uploaded)="onUploaded($event)"
></ws-upload-warranty-document-dialog>

<vs-dialog
  #removeDialog
  [config]="{ disableBackdropDismiss: removing, dialogContainerClass: 'w-[440px] max-w-[95vw]' }"
  (closed)="cancelRemoval()"
>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      title: 'Remove document',
      icon: 'file-minus-02',
      iconTheme: 'danger',
      hideCloseButton: removing,
    }"
  ></vs-dialog-header>
  <div *ngIf="removalTarget" class="p-5">
    <p class="text-sm text-gray-600">
      Remove this document from your warranty's repository? Documents already attached to claims will be retained.
    </p>
    <p class="font-semibold break-words mt-3">{{ removalTarget.title }}</p>
    <p *ngIf="removalError" role="alert" class="text-sm text-error-800 mt-3">
      Unable to remove this document. Please try again.
    </p>
    <div class="flex justify-end gap-3 mt-5">
      <button vs-button appearance="outline" type="button" [disabled]="removing" (click)="removeDialog.close()">
        Cancel
      </button>
      <button vs-button theme="danger" type="button" [disabled]="removing" (click)="removeDocument()">
        {{ removing ? 'Removing…' : 'Remove document' }}
      </button>
    </div>
  </div>
</vs-dialog>
